// Check if error should trigger wallet disconnect
const isWalletDisconnectError = (errorMessage: string): boolean => {
  return (
    errorMessage.includes('could not coalesce error') ||
    errorMessage.includes('missing trie node') ||
    errorMessage.includes('Internal JSON-RPC error') ||
    errorMessage.includes("Failed to connect") ||
    errorMessage.includes('UNKNOWN_ERROR') ||
    errorMessage.includes('network error') ||
    errorMessage.includes('connection failed') ||
    errorMessage.includes('RPC Error')
  );
};

// Helper function to check if error should trigger wallet disconnect
const shouldDisconnectWallet = (error: any): boolean => {
  const errorMessage = error?.message?.toLowerCase() || String(error).toLowerCase();
  const errorCode = error?.code;
  
  return (
    errorMessage.includes('could not coalesce error') ||
    errorMessage.includes('missing trie node') ||
    errorMessage.includes('internal json-rpc error') ||
    errorMessage.includes("Failed to connect") ||
    errorMessage.includes('network error') ||
    errorMessage.includes('connection failed') ||
    errorMessage.includes('rpc error') ||
    errorCode === -32603 ||
    errorCode === -32000 ||
    errorCode === 'UNKNOWN_ERROR'
  );
};

// Function to show wallet error notification
const showWalletErrorNotification = () => {
  // Dispatch custom event that will be caught by GlobalErrorHandler
  window.dispatchEvent(
    new CustomEvent("app-error", {
      detail: {
        message: "Wallet connection error. Wallet disconnected. Please reconnect your wallet",
        context: "Wallet Error Handler",
        isWalletError: true
      }
    })
  );
};

// Export utility function to show wallet error notifications
export const notifyWalletError = (message?: string) => {
  window.dispatchEvent(
    new CustomEvent("app-error", {
      detail: {
        message: message || "Wallet connection error. Wallet disconnected. Please reconnect your wallet",
        context: "Wallet Error",
        isWalletError: true
      }
    })
  );
};

// Global error handler for all types of runtime errors
export const setupWeb3ErrorHandler = () => {
  // Override console.error to filter out common runtime errors
  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    const errorMessage = args.join(' ');
    
    // Check if this error should trigger wallet disconnect
    if (isWalletDisconnectError(errorMessage)) {
      console.warn('Wallet connection error detected, triggering disconnect:', errorMessage);
      
      // Show notification via GlobalErrorHandler
      showWalletErrorNotification();
      
      // Trigger wallet disconnect event for backward compatibility
      window.dispatchEvent(new CustomEvent('wallet-disconnect-required', {
        detail: { 
          error: errorMessage, 
          reason: 'RPC connection error detected in console.error'
        }
      }));
      
      // Log as warning instead of error to prevent runtime error display
      console.warn('Web3 connection issue (handled with disconnect):', ...args);
      return;
    }
    
    // Filter out common Web3 connection errors
    if (
      errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('TypeError: Failed to fetch') ||
      errorMessage.includes('web3-providers-http') ||
      errorMessage.includes('web3-core') ||
      errorMessage.includes('web3-eth') ||
      errorMessage.includes('getChainId') ||
      errorMessage.includes('HttpProvider.request') ||
      errorMessage.includes('Request method eth_chainId is not supported') ||
      errorMessage.includes('Request method') ||
      errorMessage.includes('is not supported') ||
      errorMessage.includes('web3-rpc-methods') ||
      errorMessage.includes('web3_request_manager') ||
      errorMessage.includes('empty reader set')
    ) {
      // Log as warning instead of error to prevent runtime error display
      console.warn('Web3 connection issue (handled silently):', ...args);
      return;
    }
    
    // Filter out wallet extension conflicts
    if (
      errorMessage.includes('Cannot redefine property: ethereum') ||
      errorMessage.includes('Cannot assign to read only property') ||
      errorMessage.includes('chrome-extension') ||
      errorMessage.includes('inpage.js')
    ) {
      // Log as warning instead of error to prevent runtime error display
      console.warn('Wallet extension conflict (handled silently):', ...args);
      return;
    }

    if (
      errorMessage.includes('The requested method and/or account has not been authorized by the user')
    ) {
      console.warn('Authorization issue (handled silently):', ...args);
      return;
    }

    // Filter out common network and resource errors
    if (
      errorMessage.includes('net::ERR_') ||
      errorMessage.includes('NetworkError') ||
      errorMessage.includes('Load failed') ||
      errorMessage.includes('CORS') ||
      errorMessage.includes('Cross-Origin') ||
      errorMessage.includes('Mixed Content') ||
      errorMessage.includes('Blocked a frame') ||
      errorMessage.includes('Refused to connect') ||
      errorMessage.includes('ERR_CONNECTION_') ||
      errorMessage.includes('ERR_NAME_NOT_RESOLVED') ||
      errorMessage.includes('ERR_TIMED_OUT') ||
      errorMessage.includes('ERR_INTERNET_DISCONNECTED')
    ) {
      console.warn('Network error (handled silently):', ...args);
      return;
    }

    // Filter out common DOM and React errors
    if (
      errorMessage.includes('Cannot read properties of undefined') ||
      errorMessage.includes('Cannot read property') ||
      errorMessage.includes('is not a function') ||
      errorMessage.includes('Cannot access before initialization') ||
      errorMessage.includes('ReferenceError') ||
      errorMessage.includes('TypeError') ||
      errorMessage.includes('SyntaxError') ||
      errorMessage.includes('RangeError') ||
      errorMessage.includes('EvalError') ||
      errorMessage.includes('URIError') ||
      errorMessage.includes('Error: Minified React error') ||
      errorMessage.includes('React.createElement') ||
      errorMessage.includes('Element type is invalid') ||
      errorMessage.includes('Objects are not valid as a React child') ||
      errorMessage.includes('Warning:') ||
      errorMessage.includes('Invariant Violation')
    ) {
      console.warn('Runtime error (handled silently):', ...args);
      return;
    }

    // Filter out common browser compatibility errors
    if (
      errorMessage.includes('is not defined') ||
      errorMessage.includes('is not a constructor') ||
      errorMessage.includes('Cannot use import statement') ||
      errorMessage.includes('Unexpected token') ||
      errorMessage.includes('Unexpected end of input') ||
      errorMessage.includes('Unexpected identifier') ||
      errorMessage.includes('Unexpected string') ||
      errorMessage.includes('Unexpected number') ||
      errorMessage.includes('Expected') ||
      errorMessage.includes('Uncaught') ||
      errorMessage.includes('Script error') ||
      errorMessage.includes('Non-Error promise rejection')
    ) {
      console.warn('Browser compatibility error (handled silently):', ...args);
      return;
    }
    
    // Pass through other errors normally
    originalConsoleError.apply(console, args);
  };

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    
    // Check if it's a Web3 connection error
    if (isWeb3Error(error)) {
      console.warn('Web3 promise rejection handled silently:', error);
      event.preventDefault(); // Prevent the error from showing
      return;
    }
    
    // Check if it's a wallet extension conflict
    if (isExtensionConflictError(error)) {
      console.warn('Wallet extension conflict handled silently:', error);
      event.preventDefault(); // Prevent the error from showing
      return;
    }

    // Check if it's a general runtime error
    if (isRuntimeError(error)) {
      console.warn('Runtime promise rejection handled silently:', error);
      event.preventDefault(); // Prevent the error from showing
      return;
    }
  });

  // Handle global errors
  window.addEventListener('error', (event) => {
    const error = event.error;
    
    // Check if it's a Web3 connection error
    if (isWeb3Error(error)) {
      console.warn('Web3 runtime error handled silently:', error);
      event.preventDefault(); // Prevent the error from showing
      return;
    }
    
    // Check if it's a wallet extension conflict
    if (isExtensionConflictError(error)) {
      console.warn('Wallet extension conflict handled silently:', error);
      event.preventDefault(); // Prevent the error from showing
      return;
    }

    // Check if it's a general runtime error
    if (isRuntimeError(error)) {
      console.warn('Runtime error handled silently:', error);
      event.preventDefault(); // Prevent the error from showing
      return;
    }
  });
};

// Function to safely execute Web3 operations
export const safeWeb3Operation = async <T>(
  operation: () => Promise<T>,
  fallbackValue: T,
  operationName: string = 'Web3 operation'
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    console.warn(`${operationName} failed, using fallback value:`, error);
    return fallbackValue;
  }
};

// Function to check if error is Web3-related
export const isWeb3Error = (error: any): boolean => {
  if (!error) return false;
  
  const errorString = String(error);
  return (
    errorString.includes('Failed to fetch') ||
    errorString.includes('TypeError: Failed to fetch') ||
    errorString.includes('web3-providers-http') ||
    errorString.includes('web3-core') ||
    errorString.includes('web3-eth') ||
    errorString.includes('getChainId') ||
    errorString.includes('HttpProvider.request') ||
    errorString.includes('Request method eth_chainId is not supported') ||
    errorString.includes('Request method') ||
    errorString.includes('is not supported') ||
    errorString.includes('web3-rpc-methods') ||
    errorString.includes('web3_request_manager') ||
    errorString.includes('empty reader set') ||
    errorString.includes('Internal JSON-RPC error') ||
    errorString.includes('UNKNOWN_ERROR') ||
    errorString.includes("Failed to connect") ||
    errorString.includes('could not coalesce error')
  );
};

// Function to check if error is a runtime error that should be handled silently
export const isRuntimeError = (error: any): boolean => {
  if (!error) return false;
  
  const errorString = String(error);
  return (
    // Network errors
    errorString.includes('net::ERR_') ||
    errorString.includes('NetworkError') ||
    errorString.includes('Load failed') ||
    errorString.includes('CORS') ||
    errorString.includes('Cross-Origin') ||
    errorString.includes('Mixed Content') ||
    errorString.includes('Blocked a frame') ||
    errorString.includes('Refused to connect') ||
    errorString.includes('ERR_CONNECTION_') ||
    errorString.includes('ERR_NAME_NOT_RESOLVED') ||
    errorString.includes('ERR_TIMED_OUT') ||
    errorString.includes('ERR_INTERNET_DISCONNECTED') ||
    
    // DOM and React errors
    errorString.includes('Cannot read properties of undefined') ||
    errorString.includes('Cannot read property') ||
    errorString.includes('is not a function') ||
    errorString.includes('Cannot access before initialization') ||
    errorString.includes('ReferenceError') ||
    errorString.includes('TypeError') ||
    errorString.includes('SyntaxError') ||
    errorString.includes('RangeError') ||
    errorString.includes('EvalError') ||
    errorString.includes('URIError') ||
    errorString.includes('Error: Minified React error') ||
    errorString.includes('React.createElement') ||
    errorString.includes('Element type is invalid') ||
    errorString.includes('Objects are not valid as a React child') ||
    errorString.includes('Warning:') ||
    errorString.includes('Invariant Violation') ||
    
    // Browser compatibility errors
    errorString.includes('is not defined') ||
    errorString.includes('is not a constructor') ||
    errorString.includes('Cannot use import statement') ||
    errorString.includes('Unexpected token') ||
    errorString.includes('Unexpected end of input') ||
    errorString.includes('Unexpected identifier') ||
    errorString.includes('Unexpected string') ||
    errorString.includes('Unexpected number') ||
    errorString.includes('Expected') ||
    errorString.includes('Uncaught') ||
    errorString.includes('Script error') ||
    errorString.includes('Non-Error promise rejection')
  );
};

// Function to check if error is a wallet extension conflict
export const isExtensionConflictError = (error: any): boolean => {
  if (!error) return false;
  
  const errorString = String(error);
  return (
    errorString.includes('Cannot redefine property: ethereum') ||
    errorString.includes('Cannot assign to read only property') ||
    errorString.includes('chrome-extension') ||
    errorString.includes('inpage.js')
  );
};

// Function to safely handle wallet extension conflicts
export const safeWalletExtensionSetup = () => {
  try {
    // Check if ethereum is already defined and make it writable
    if (window.ethereum && Object.isFrozen(window.ethereum)) {
      console.warn('Ethereum object is frozen, attempting to make it writable');
      try {
        // Try to make the ethereum object writable
        Object.defineProperty(window, 'ethereum', {
          value: window.ethereum,
          writable: true,
          configurable: true
        });
      } catch (e) {
        console.warn('Could not make ethereum object writable:', e);
      }
    }
  } catch (error) {
    console.warn('Wallet extension setup failed:', error);
  }
};

// Function to safely execute any operation with comprehensive error handling
export const safeExecute = async <T>(
  operation: () => Promise<T> | T,
  fallbackValue: T,
  operationName: string = 'Operation'
): Promise<T> => {
  try {
    const result = await operation();
    return result;
  } catch (error) {
    console.warn(`${operationName} failed, using fallback value:`, error);
    return fallbackValue;
  }
};

// Function to safely execute synchronous operations
export const safeExecuteSync = <T>(
  operation: () => T,
  fallbackValue: T,
  operationName: string = 'Operation'
): T => {
  try {
    return operation();
  } catch (error) {
    console.warn(`${operationName} failed, using fallback value:`, error);
    return fallbackValue;
  }
};

// Function to create a safe wrapper for event handlers
export const safeEventHandler = <T extends (...args: any[]) => any>(
  handler: T,
  fallback?: () => void
): T => {
  return ((...args: any[]) => {
    try {
      return handler(...args);
    } catch (error) {
      console.warn('Event handler error (handled silently):', error);
      if (fallback) {
        fallback();
      }
    }
  }) as T;
};

// Function to safely access nested object properties
export const safeGet = <T>(
  obj: any,
  path: string,
  defaultValue: T
): T => {
  try {
    const keys = path.split('.');
    let result = obj;
    for (const key of keys) {
      if (result == null || typeof result !== 'object') {
        return defaultValue;
      }
      result = result[key];
    }
    return result !== undefined ? result : defaultValue;
  } catch (error) {
    console.warn('Safe get error (handled silently):', error);
    return defaultValue;
  }
};

// Function to safely call methods on objects
export const safeCall = <T>(
  obj: any,
  methodName: string,
  args: any[] = [],
  defaultValue: T
): T => {
  try {
    if (obj && typeof obj[methodName] === 'function') {
      return obj[methodName](...args);
    }
    return defaultValue;
  } catch (error) {
    console.warn('Safe call error (handled silently):', error);
    return defaultValue;
  }
};

// Function to setup comprehensive error monitoring
export const setupErrorMonitoring = () => {
  // Monitor for resource loading errors
  window.addEventListener('error', (event) => {
    if (event.target !== window) {
      // This is a resource loading error (image, script, etc.)
      console.warn('Resource loading error (handled silently):', event);
      event.preventDefault();
    }
  }, true);

  // Monitor for unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.warn('Unhandled promise rejection (handled silently):', event.reason);
    event.preventDefault();
  });

  // Monitor for console errors
  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    const errorMessage = args.join(' ');
    
    // Check if it's an error we should handle silently
    if (isWeb3Error({ message: errorMessage }) || 
        isExtensionConflictError({ message: errorMessage }) || 
        isRuntimeError({ message: errorMessage })) {
      console.warn('Error handled silently:', ...args);
      return;
    }
    
    // Pass through other errors
    originalConsoleError.apply(console, args);
  };
};

// Setup global error listeners for wallet disconnection
export const setupWalletErrorListeners = () => {
  // Global unhandled error listener
  window.addEventListener('error', (event) => {
    const error = event.error;
    
    if (shouldDisconnectWallet(error)) {
      console.warn('Unhandled wallet error detected, triggering disconnect:', error);
      
      // Prevent the error from showing in console
      event.preventDefault();
      
      // Show notification via GlobalErrorHandler
      showWalletErrorNotification();
      
      // Trigger wallet disconnect for backward compatibility
      window.dispatchEvent(new CustomEvent('wallet-disconnect-required', {
        detail: { error, reason: 'Unhandled RPC error' }
      }));
    }
  });

  // Global unhandled promise rejection listener
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    
    if (shouldDisconnectWallet(error)) {
      console.warn('Unhandled promise rejection (wallet error), triggering disconnect:', error);
      
      // Prevent the error from showing
      event.preventDefault();
      
      // Show notification via GlobalErrorHandler
      showWalletErrorNotification();
      
      // Trigger wallet disconnect for backward compatibility
      window.dispatchEvent(new CustomEvent('wallet-disconnect-required', {
        detail: { error, reason: 'Unhandled promise rejection' }
      }));
    }
  });
};
